import Feature from "ol/Feature";
import Geometry from "ol/geom/Geometry";
import Point from "ol/geom/Point";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { SeaDto, WaveDto, WindDto } from "~/api/GeneratedApi";
import { SeaMapItem, WaveMapItem, WindMapItem } from "../types/mapItems";

type UseWeatherLayerHelpersReturn = {
  windToFeatures: (d: WindDto[]) => Feature<Geometry>[];
  seaToFeatures: (d: SeaDto[]) => Feature<Geometry>[];
  waveToFeatures: (d: WaveDto[]) => Feature<Geometry>[];
};

export function useWeatherLayerHelpers(): UseWeatherLayerHelpersReturn {
  const { t } = useTranslation("map");
  const windToFeatures = useCallback(
    (data: WindDto[]): Feature<Geometry>[] => {
      // Map to internal type
      const finalFeatures: Feature<Geometry>[] = [];
      data.forEach((d) => {
        if (!d.location) return;
        const point = new Point([d.location.x, d.location.y]);

        const speedMeas = d.windSpeedMeasurements;

        const dirMeas = d.windDirectionMeasurements;

        const gustMeas = d.windGustMeasurements;

        const properties: WindMapItem = {
          ...d,
          type: "wind",
          name: `${t("wind")} - ${d.stationName}`,
          mapId: `wind${d.id}`,
          windSpeedMeasurements: speedMeas,
          latestSpeedValue: speedMeas?.[0],
          windDirectionMeasurements: dirMeas,
          latestDirectionValue: dirMeas?.[0],
          windGustMeasurements: gustMeas,
          latestGustValue: gustMeas?.[0],
        };
        const feat = new Feature();
        feat.setGeometry(point);
        feat.setId(d.id);
        feat.setProperties({ ...properties });
        finalFeatures.push(feat);
      });

      return finalFeatures;
    },
    [t],
  );

  const seaToFeatures = useCallback(
    (data: SeaDto[]): Feature<Geometry>[] => {
      // Map to internal type
      const finalFeatures: Feature<Geometry>[] = [];
      data.forEach((d) => {
        if (!d.location) return;
        const point = new Point([d.location.x, d.location.y]);

        const levelMeas = d.seaLevelMeasurements;

        const temperatureMeas = d.seaTemperatureMeasurements;

        const properties: SeaMapItem = {
          ...d,
          type: "sea",
          name: `${t("seaLevel")} - ${d.stationName}`,
          mapId: `sea${d.id}`,
          seaLevelMeasurements: levelMeas,
          latestLevelValue: levelMeas?.[0],
          seaTemperatureMeasurements: temperatureMeas,
          latestTemperatureValue: temperatureMeas?.[0],
        };
        const feat = new Feature();
        feat.setGeometry(point);
        feat.setId(d.id);
        feat.setProperties({ ...properties });
        finalFeatures.push(feat);
      });

      return finalFeatures;
    },
    [t],
  );

  const waveToFeatures = useCallback(
    (data: WaveDto[]): Feature<Geometry>[] => {
      // Map to internal type
      const finalFeatures: Feature<Geometry>[] = [];
      data.forEach((d) => {
        if (!d.location) return;
        const point = new Point([d.location.x, d.location.y]);

        const dirMeas = d.waveDirectionMeasurements;

        const heightMeas = d.waveHeightMeasurements;

        const properties: WaveMapItem = {
          ...d,
          type: "wave",
          name: `${t("waveHeight")} - ${d.stationName}`,
          mapId: `wave${d.id}`,
          waveDirectionMeasurements: dirMeas,
          latestDirectionValue: dirMeas?.[0],
          waveHeightMeasurements: heightMeas,
          latestHeightValue: heightMeas?.[0],
        };
        const feat = new Feature();
        feat.setGeometry(point);
        feat.setId(d.id);
        feat.setProperties({ ...properties });
        finalFeatures.push(feat);
      });

      return finalFeatures;
    },
    [t],
  );

  return { windToFeatures, seaToFeatures, waveToFeatures };
}
